import { styled } from '@mui/system';
import React, {
  ComponentType,
  DragEvent as ReactDragEvent,
  forwardRef,
  HTMLAttributes,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  RefObject,
} from 'react';

export interface DropzoneProps extends PropsWithChildren {
  accept?: string[];
  onFileDrop: (files: File[]) => void;
  onDragEnter?: () => void;
  onDragLeave?: () => void;
  onFilesFiltered?: (files: File[]) => void;
  noClick?: true;
  disabled?: boolean;
  inputRef?: RefObject<HTMLInputElement | null>;
  multiple?: boolean;
};

const dragOverBlocker = (e: DragEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const FileInput = styled('input')({
  visibility: 'hidden',
  width: '0px',
  height: '0px',
  position: 'absolute',
});

const isExtension = /^\.\w+$/i;
const isMimetype = /^([^\/\s]+)\/([^\/\s]+)/i;

export default function <T extends HTMLAttributes<HTMLElement>>(Component: ComponentType<T>) {
  return forwardRef<HTMLElement, T & DropzoneProps>(
    (
      {
        children,
        accept,
        onFileDrop,
        onDragEnter,
        onDragLeave,
        onFilesFiltered,
        noClick,
        disabled,
        inputRef: inputOuterRef,
        multiple = true,
        ...props
      },
      ref: RefObject<HTMLElement>
    ) => {
      const mainRef = useRef<HTMLElement>(null);

      const inputRef = useRef<HTMLInputElement>(null);

      const clickHandler = useCallback((e: MouseEvent) => {
        if (inputRef.current && !disabled && !noClick) {
          inputRef.current.click();
        }
      }, [disabled, noClick]);

      const [extensions, mimeTypes] = useMemo(() => {
        const extensions: string[] = [];
        const mimeTypes: [string, string][] = [];

        if (accept) {
          for (const v of accept) {
            const c = v.trim();
            if (isExtension.test(c)) {
              extensions.push(c.toLowerCase());
            } else {
              const parts = c.match(isMimetype);
              if (parts) {
                mimeTypes.push([
                  parts[1].toLowerCase(),
                  parts[2].toLowerCase()
                ]);
              }
            }
          }
        }

        return [extensions, mimeTypes];
      }, [accept]);

      const fileFilter = useCallback(
        (f: File) => {
          if (!accept?.length) return true;
          const ext = ((f.name.match(/\.\w+$/i) || [])[0] || '').toLowerCase();
          if (extensions.includes(ext)) return true;
          if (!f.type) return false;
          const parts = f.type.match(isMimetype);
          if (!parts) return false;
          const type = (parts[1] || '').toLowerCase(),
                subtype = (parts[2] || '').toLowerCase();
          if (mimeTypes.find(([t, st]) => (t === type && (st === '*' || st === subtype)))) return true;
          return false;
        },
        [accept, extensions, mimeTypes]
      );

      const addFiles = useCallback((files: File[]) => {
        if (disabled) {
          return;
        }

        if (!multiple) {
          files = [files[0]];
        }

        const excluded: File[] = [];
        const included = files.filter((f) => {
          const r = fileFilter(f);
          if (!r) {
            excluded.push(f);
          }
          return r;
        });
        if (excluded.length && onFilesFiltered) {
          onFilesFiltered(excluded);
        }
        onFileDrop(included);
      }, [ fileFilter, onFileDrop, onFilesFiltered, multiple, disabled ]);

      const changeHandler = useCallback(() => {
        if (inputRef.current && inputRef.current.files?.length) {
          addFiles(Array.from(inputRef.current.files));
          inputRef.current.files = null;
          inputRef.current.value = '';
        }
      }, [ inputRef.current, addFiles]);
    
      const dropHandler = useCallback((e: ReactDragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.dataTransfer?.files) {
          addFiles(Array.from(e.dataTransfer?.files));
        }
      }, [ addFiles ]);

      const dragEnterHandler = useCallback((e: ReactDragEvent) => {
        if (mainRef.current && !disabled) {
          if (mainRef.current.contains(e.relatedTarget as HTMLElement)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (onDragEnter) {
            onDragEnter();
          }
        }
      }, [onDragEnter, disabled]);

      const dragLeaveHandler = useCallback((e: ReactDragEvent) => {
        if (mainRef.current && !disabled) {
          if (mainRef.current.contains(e.relatedTarget as HTMLElement)) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          if (onDragLeave) {
            onDragLeave();
          }
        }
      }, [onDragLeave, disabled]);

      useEffect(() => {
        if (mainRef.current) {
          const root = mainRef.current;
          root.addEventListener('dragover', dragOverBlocker);
        }
      }, [mainRef.current, onDragEnter, onDragLeave, dropHandler, noClick, disabled]);

      useEffect(() => {
        if (mainRef.current && ref && ref.current !== mainRef.current) {
          ref.current = mainRef.current;
        }
      }, [mainRef.current, ref]);

      useEffect(() => {
        if (inputRef.current && inputOuterRef && inputOuterRef.current !== inputRef.current) {
          inputOuterRef.current = inputRef.current;
        }
      }, [ inputRef.current, inputOuterRef ]);

      return (
        <>
          <Component
            {...props as unknown as T}
            ref={mainRef}
            onDragEnter={dragEnterHandler}
            onDragLeave={dragLeaveHandler}
            onDrop={dropHandler}
            onClick={clickHandler}
          >
            {children}
          </Component>
          {(!noClick || inputOuterRef) && <FileInput ref={inputRef} accept={(accept || []).join(',')} type="file" onChange={changeHandler} multiple={multiple}/>}
        </>
      );
    }
  );
}
